import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { salesItemService, CreateSalesItemRequest } from '../../services/salesItemService';
import { productService } from '../../services/productService';
import taxService, { Tax } from '../../services/taxService';
import { useCurrency } from '../../hooks/useCurrency';
import { parseApiError } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import '../Products/EntityCreatePage.css';
import { ArrowLeft } from 'lucide-react';

interface FieldErrors {
  product?: string;
  sellingPrice?: string;
  minimumSellingPrice?: string;
  returnPeriodDays?: string;
}

const SalesItemCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedProductId = (location.state as { productId?: number } | null)?.productId;
  const { t } = useTranslation();
  const defaultCurrency = useCurrency();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [products, setProducts] = useState<any[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [formData, setFormData] = useState<CreateSalesItemRequest>({
    productId: preselectedProductId || 0,
    sellingPrice: 0,
    currency: defaultCurrency,
    discountAllowed: true,
    returnAllowed: true,
    returnPeriodDays: 7,
    salesChannel: 'Store',
  });

  useEffect(() => {
    loadProducts();
    taxService.getTaxes(1, 200).then(res => {
      const list: Tax[] = res?.data ?? [];
      setTaxes(list.filter(tx => tx.isActive && (tx.applicableFor === 'Sales' || tx.applicableFor === 'Both')));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // Update currency when default changes
    setFormData(prev => ({ ...prev, currency: defaultCurrency }));
  }, [defaultCurrency]);

  const loadProducts = async () => {
    try {
      const response = await productService.getProducts({ pageSize: 1000 });
      if (response.data.data) {
        setProducts(response.data.data as any[]);
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const isCheckbox = type === 'checkbox';
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: isCheckbox
        ? checked
        : name === 'productId' || name === 'taxId'
        ? parseInt(value) || undefined
        : name === 'returnPeriodDays'
        ? parseInt(value) || 0
        : ['sellingPrice', 'minimumSellingPrice'].includes(name)
        ? parseFloat(value) || 0
        : value,
    }));

    if (!isCheckbox) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    // Product required
    if (!formData.productId) {
      errors.product = t('salesItem.formValidation.productRequired');
      isValid = false;
    }

    // Selling price >= 0
    if (formData.sellingPrice < 0) {
      errors.sellingPrice = t('salesItem.formValidation.sellingPriceNegative');
      isValid = false;
    }

    // Minimum selling price: >= 0 and must not exceed selling price
    if (formData.minimumSellingPrice !== undefined && formData.minimumSellingPrice !== null) {
      if (formData.minimumSellingPrice < 0) {
        errors.minimumSellingPrice = t('salesItem.formValidation.minimumSellingPriceNegative');
        isValid = false;
      } else if (formData.minimumSellingPrice > formData.sellingPrice) {
        errors.minimumSellingPrice = t('salesItem.formValidation.minPriceExceedsSelling');
        isValid = false;
      }
    }

    // Return period >= 0
    if (formData.returnAllowed && formData.returnPeriodDays !== undefined && formData.returnPeriodDays < 0) {
      errors.returnPeriodDays = t('salesItem.formValidation.returnPeriodNegative');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    if (!validateForm()) return;

    setLoading(true);
    try {
      await salesItemService.createSalesItem(formData);
      navigate('/sales-items', { state: { message: t('salesItem.itemCreatedSuccess') } });
    } catch (err: unknown) {
      setError(parseApiError(err, t('salesItem.failedToCreateItem')).message);
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="create-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/sales-items')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('salesItem.createTitle')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/sales-items')} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={loading}>
              {loading ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : t('salesItem.createItem')}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="create-content">
          <div className="create-main-section">
            <div className="create-card">
              <div className="create-card-header">
                <h2 className="create-card-title">{t('salesItem.itemDetails')}</h2>
              </div>
              <div className="create-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>

                  {/* Product */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('salesItem.product')} <span className="required">*</span>
                    </label>
                    <select
                      className={`form-select ${fieldErrors.product ? 'is-invalid' : ''}`}
                      name="productId"
                      value={formData.productId || ''}
                      onChange={handleInputChange}
                      disabled={!!preselectedProductId}
                    >
                      <option value="">-- {t('salesItem.product')} --</option>
                      {products.map((prod) => (
                        <option key={prod.id || prod.productId} value={prod.id || prod.productId}>
                          {prod.sku || prod.productSKU} - {prod.name || prod.productName}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.product && <small className="form-error">{fieldErrors.product}</small>}
                  </div>

                  {/* Selling Price */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('salesItem.sellingPrice')} <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-input ${fieldErrors.sellingPrice ? 'is-invalid' : ''}`}
                      name="sellingPrice"
                      value={formData.sellingPrice}
                      onChange={handleInputChange}
                      step="0.01"
                    />
                    {fieldErrors.sellingPrice && <small className="form-error">{fieldErrors.sellingPrice}</small>}
                    {!fieldErrors.sellingPrice && <small className="form-helper">{t('salesItem.sellingPriceHint')}</small>}
                  </div>

                  {/* Minimum Selling Price */}
                  <div className="form-group">
                    <label className="form-label">{t('salesItem.minimumSellingPrice')}</label>
                    <input
                      type="number"
                      className={`form-input ${fieldErrors.minimumSellingPrice ? 'is-invalid' : ''}`}
                      name="minimumSellingPrice"
                      value={formData.minimumSellingPrice || ''}
                      onChange={handleInputChange}
                      step="0.01"
                    />
                    {fieldErrors.minimumSellingPrice && <small className="form-error">{fieldErrors.minimumSellingPrice}</small>}
                    {!fieldErrors.minimumSellingPrice && <small className="form-helper">{t('salesItem.minimumSellingPriceHint')}</small>}
                  </div>

                  {/* Currency */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('salesItem.currency')} <span className="required">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="JPY">JPY</option>
                      <option value="INR">INR</option>
                      <option value="LKR">LKR</option>
                    </select>
                  </div>

                  {/* Sales Channel */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('salesItem.salesChannel')} <span className="required">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="salesChannel"
                      value={formData.salesChannel}
                      onChange={handleInputChange}
                    >
                      <option value="Store">{t('salesItem.channelStore')}</option>
                      <option value="Online">{t('salesItem.channelOnline')}</option>
                      <option value="Wholesale">{t('salesItem.channelWholesale')}</option>
                      <option value="Distributor">{t('salesItem.channelDistributor')}</option>
                    </select>
                  </div>

                  {/* Tax */}
                  <div className="form-group">
                    <label className="form-label">{t('salesItem.tax')}</label>
                    <select
                      className="form-select"
                      name="taxId"
                      value={formData.taxId ?? ''}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('salesItem.noTax')}</option>
                      {taxes.map((tax) => (
                        <option key={tax.taxId} value={tax.taxId}>
                          {tax.taxName} ({tax.taxPercentage}%)
                        </option>
                      ))}
                    </select>
                    {formData.taxId && (() => {
                      const selected = taxes.find(tx => tx.taxId === formData.taxId);
                      return selected ? (
                        <small className="form-helper">
                          {t('salesItem.taxHint')} &mdash; <strong>{selected.taxPercentage}%</strong>
                        </small>
                      ) : null;
                    })()}
                    {!formData.taxId && <small className="form-helper">{t('salesItem.taxHint')}</small>}
                  </div>

                  {/* Warranty Period */}
                  <div className="form-group">
                    <label className="form-label">{t('salesItem.warrantyPeriod')}</label>
                    <input
                      type="text"
                      className="form-input"
                      name="warrantyPeriod"
                      value={formData.warrantyPeriod || ''}
                      onChange={handleInputChange}
                      placeholder={t('salesItem.warrantyPeriodPlaceholder')}
                    />
                  </div>

                  {/* Discount Allowed */}
                  <div className="form-group">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="discountAllowed"
                        name="discountAllowed"
                        checked={formData.discountAllowed}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="discountAllowed">
                        {t('salesItem.discountAllowed')}
                      </label>
                    </div>
                  </div>

                  {/* Return Allowed */}
                  <div className="form-group">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="returnAllowed"
                        name="returnAllowed"
                        checked={formData.returnAllowed}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="returnAllowed">
                        {t('salesItem.returnAllowed')}
                      </label>
                    </div>
                  </div>

                  {/* Return Period Days */}
                  {formData.returnAllowed && (
                    <div className="form-group">
                      <label className="form-label">{t('salesItem.returnPeriodDays')}</label>
                      <input
                        type="number"
                        className={`form-input ${fieldErrors.returnPeriodDays ? 'is-invalid' : ''}`}
                        name="returnPeriodDays"
                        value={formData.returnPeriodDays || ''}
                        onChange={handleInputChange}
                      />
                      {fieldErrors.returnPeriodDays && <small className="form-error">{fieldErrors.returnPeriodDays}</small>}
                      {!fieldErrors.returnPeriodDays && <small className="form-helper">{t('salesItem.returnPeriodDaysHint')}</small>}
                    </div>
                  )}


                </form>
              </div>
            </div>
          </div>

          <div className="create-sidebar">
            <div className="create-card">
              <div className="create-card-header">
                <h3 className="create-card-title">{t('common.information')}</h3>
              </div>
              <div className="create-card-body">
                <p className="create-info-text">
                  <strong>{t('salesItem.requiredFields')}</strong>
                </p>
                <ul className="create-info-list">
                  <li>{t('salesItem.product')}</li>
                  <li>{t('salesItem.sellingPrice')}</li>
                  <li>{t('salesItem.currency')}</li>
                  <li>{t('salesItem.salesChannel')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SalesItemCreatePage;
